import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Dialog, Heading, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';

import type { ShortcutDefinition } from '../types';

import { KeyboardShortcut } from './keyboard-shortcut';

import styles from './keyboard-shortcuts-dialog.module.css';

export interface KeyboardShortcutsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts: ShortcutDefinition[];
}

/**
 * Dialog component for displaying all keyboard shortcuts.
 * Groups shortcuts by category and shows formatted hotkeys with labels.
 */
export function KeyboardShortcutsDialog({ isOpen, onOpenChange, shortcuts }: KeyboardShortcutsDialogProps) {
  // Group shortcuts by category
  const groupedShortcuts: Record<string, ShortcutDefinition[]> = {};
  for (const shortcut of shortcuts) {
    const category = shortcut.category || 'General';
    if (!groupedShortcuts[category]) {
      groupedShortcuts[category] = [];
    }
    groupedShortcuts[category].push(shortcut);
  }

  // Sort categories with General first
  const sortedCategories = Object.keys(groupedShortcuts).sort((a, b) => {
    if (a === 'General') {
      return -1;
    }
    if (b === 'General') {
      return 1;
    }
    return a.localeCompare(b);
  });

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal>
        <Dialog aria-label={msg('keyboardShortcuts.shortcutsDialog.heading')} className={styles.dialog}>
          {({ close }) => (
            <>
              <div slot="header">
                <Heading>
                  <Message id="keyboardShortcuts.shortcutsDialog.heading" />
                </Heading>
                <IconButton aria-label="close" icon="cancel" size="small" variant="minimal" onPress={close} />
              </div>
              <div slot="body">
                {sortedCategories.map((category) => (
                  <section key={category}>
                    <h3>{category}</h3>
                    <ul className={styles.list}>
                      {groupedShortcuts[category].map((shortcut) => (
                        <li className={styles.item} key={shortcut.id}>
                          <span>{shortcut.label}</span>
                          <KeyboardShortcut shortcut={shortcut.hotkey} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
